<template>
    <div class="p-6 bg-gray-50 min-h-screen">
        <!-- Header avec recherche et filtres -->
        <BaseCard padding="md" class="mb-6">
            <div class="flex flex-col md:flex-row gap-4 items-center justify-between">
                <!-- Barre de recherche -->
                <div class="w-full md:w-96">
                    <BaseInput
                        v-model="search"
                        type="search"
                        placeholder="Rechercher un produit..."
                        clearable
                        @input="onSearchInput"
                    >
                        <template #icon-left>
                            <BaseIcon name="search" size="18" color="#64748b" />
                        </template>
                    </BaseInput>
                </div>

                <!-- Filtres et actions -->
                <div class="flex gap-3 w-full md:w-auto">
                    <!-- Filtre catégorie -->
                    <div class="relative flex-1 md:flex-initial md:w-52" v-click-outside="closeCategoryDropdown">
                        <BaseInput
                            v-model="categorySearchTerm"
                            type="text"
                            placeholder="Filtrer par catégorie..."
                            @focus="showCategoryDropdown = true"
                            @input="filterCategories"
                        >
                            <template #icon-right>
                                <button
                                    v-if="selectedCategory"
                                    @click="clearCategoryFilter"
                                    class="p-1 hover:bg-gray-100 rounded"
                                >
                                    <BaseIcon name="close" size="16" />
                                </button>
                                <button
                                    v-else
                                    @click="showCategoryDropdown = !showCategoryDropdown"
                                    class="p-1"
                                >
                                    <BaseIcon name="chevron-down" size="16" />
                                </button>
                            </template>
                        </BaseInput>

                        <!-- Dropdown catégories -->
                        <transition
                            enter-active-class="transition ease-out duration-100"
                            enter-from-class="opacity-0 scale-95"
                            enter-to-class="opacity-100 scale-100"
                            leave-active-class="transition ease-in duration-75"
                            leave-from-class="opacity-100 scale-100"
                            leave-to-class="opacity-0 scale-95"
                        >
                            <div
                                v-if="showCategoryDropdown"
                                class="absolute top-full left-0 w-full max-h-52 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-10 mt-1"
                            >
                                <div v-if="filteredCategories.length === 0" class="p-3 text-center text-gray-500 text-sm">
                                    Aucune catégorie trouvée
                                </div>
                                <ul v-else class="py-1">
                                    <li
                                        @click="selectCategory('')"
                                        :class="[
                                            'px-3 py-2 cursor-pointer text-sm transition-colors border-b border-gray-200 font-medium',
                                            selectedCategory === '' ? 'bg-blue-50 text-primary' : 'hover:bg-gray-50'
                                        ]"
                                    >
                                        Toutes les catégories
                                    </li>
                                    <li
                                        v-for="category in filteredCategories"
                                        :key="category.id"
                                        @click="selectCategory(category.id)"
                                        :class="[
                                            'px-3 py-2 cursor-pointer text-sm transition-colors',
                                            selectedCategory === category.id ? 'bg-blue-50 text-primary font-medium' : 'hover:bg-gray-50'
                                        ]"
                                    >
                                        {{ category.name }}
                                    </li>
                                </ul>
                            </div>
                        </transition>
                    </div>

                    <!-- Bouton Ajouter -->
                    <BaseButton variant="primary" @click="onAddProduct">
                        <template #icon-left>
                            <BaseIcon name="plus" size="16" />
                        </template>
                        Ajouter un produit
                    </BaseButton>
                </div>
            </div>
        </BaseCard>

        <!-- Alerte stock faible -->
        <div v-if="lowStockProducts && lowStockProducts.length > 0" class="bg-yellow-50 border-l-4 border-warning p-4 mb-6 rounded-lg flex items-center justify-between">
            <div class="flex items-center gap-3">
                <BaseIcon name="exclamation-circle" size="20" color="#ffc107" />
                <span class="text-sm font-medium text-yellow-800">
                    {{ lowStockProducts.length }} produit{{ lowStockProducts.length > 1 ? 's sont' : ' est' }} en stock faible
                </span>
            </div>
            <BaseButton variant="ghost" size="sm" @click="toggleLowStockFilter">
                {{ showLowStock ? 'Voir tous les produits' : 'Voir produits en stock faible' }}
            </BaseButton>
        </div>

        <!-- Filtres actifs -->
        <div v-if="hasActiveFilters" class="flex flex-wrap items-center gap-2 mb-6 p-4 bg-gray-100 rounded-lg border border-gray-200">
            <span class="text-sm font-medium text-gray-700">Filtres actifs:</span>

            <BaseBadge v-if="search" variant="primary" closable @close="clearSearch">
                Recherche: "{{ search }}"
            </BaseBadge>

            <BaseBadge v-if="selectedCategoryName" variant="info" closable @close="clearCategoryFilter">
                Catégorie: {{ selectedCategoryName }}
            </BaseBadge>

            <BaseBadge v-if="showLowStock" variant="warning" closable @close="toggleLowStockFilter">
                Stock faible
            </BaseBadge>

            <BaseButton variant="ghost" size="xs" @click="clearAllFilters" class="ml-auto">
                Effacer tous les filtres
            </BaseButton>
        </div>

        <!-- Loader -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-20">
            <div class="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
            <p class="text-gray-600">Chargement des produits...</p>
        </div>

        <!-- Message aucun produit -->
        <div v-else-if="!loading && displayedProducts.length === 0" class="text-center py-20">
            <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BaseIcon name="document" size="32" color="#9ca3af" />
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-2">Aucun produit trouvé</h3>
            <p class="text-gray-600 mb-4">
                <span v-if="hasActiveFilters">Aucun produit ne correspond à vos critères de recherche.</span>
                <span v-else-if="showLowStock">Tous vos produits ont un stock suffisant.</span>
                <span v-else>Utilisez le bouton "Ajouter un produit" pour commencer.</span>
            </p>
            <BaseButton v-if="hasActiveFilters" variant="primary" @click="clearAllFilters">
                Réinitialiser les filtres
            </BaseButton>
        </div>

        <!-- Tableau des produits -->
        <BaseCard v-else padding="none" class="overflow-hidden">
            <div class="overflow-x-auto">
                <table class="w-full">
                    <thead class="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Image</th>
                            <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Nom du produit</th>
                            <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Quantité</th>
                            <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Catégorie</th>
                            <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Prix TTC</th>
                            <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Prix HT</th>
                            <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                        <tr
                            v-for="product in displayedProducts"
                            :key="product.id"
                            :class="[
                                'hover:bg-gray-50 transition-colors',
                                product.stock_quantity && product.stock_quantity <= 5 ? 'bg-yellow-50/50' : ''
                            ]"
                        >
                            <!-- Image -->
                            <td class="px-6 py-4">
                                <div class="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                                    <img
                                        v-if="product.image_url"
                                        :src="product.image_url"
                                        :alt="product.name"
                                        class="w-full h-full object-cover"
                                    />
                                    <BaseIcon v-else name="document" size="24" color="#d1d5db" />
                                </div>
                            </td>

                            <!-- Nom -->
                            <td
                                @click="goToProductDetails(product)"
                                class="px-6 py-4 cursor-pointer"
                            >
                                <span class="text-sm font-medium text-primary hover:underline">
                                    {{ product.name }}
                                </span>
                            </td>

                            <!-- Quantité -->
                            <td class="px-6 py-4">
                                <span class="text-sm text-gray-900">{{ product.stock_quantity }}</span>
                            </td>

                            <!-- Catégorie -->
                            <td class="px-6 py-4">
                                <BaseBadge
                                    v-if="getCategoryName(product.category_id)"
                                    variant="secondary"
                                    size="sm"
                                >
                                    {{ getCategoryName(product.category_id) }}
                                </BaseBadge>
                                <span v-else class="text-sm text-gray-400">-</span>
                            </td>

                            <!-- Prix TTC -->
                            <td class="px-6 py-4">
                                <span class="text-sm font-medium text-gray-900">
                                    {{ formatPrice(product.price_incl_tax) }}
                                </span>
                            </td>

                            <!-- Prix HT -->
                            <td class="px-6 py-4">
                                <span class="text-sm text-gray-600">
                                    {{ formatPrice(product.price_excl_tax) }}
                                </span>
                            </td>

                            <!-- Actions -->
                            <td class="px-6 py-4">
                                <div class="flex items-center gap-2">
                                    <BaseButton
                                        variant="icon"
                                        size="sm"
                                        @click.stop="goToProductDetails(product)"
                                        title="Voir les détails"
                                    >
                                        <BaseIcon name="eye" size="16" />
                                    </BaseButton>

                                    <BaseButton
                                        variant="icon"
                                        size="sm"
                                        @click.stop="onEditProduct(product)"
                                        title="Modifier"
                                    >
                                        <BaseIcon name="edit" size="16" />
                                    </BaseButton>

                                    <BaseButton
                                        variant="icon"
                                        size="sm"
                                        @click.stop="onDeleteProduct(product)"
                                        title="Supprimer"
                                        class="text-danger hover:bg-red-50"
                                    >
                                        <BaseIcon name="trash" size="16" color="#dc3545" />
                                    </BaseButton>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <PaginationComponent
                :current-page="pagination.currentPage"
                :per-page="pagination.perPage"
                :total="pagination.total"
                :to="pagination.to"
                :from="pagination.from"
                @page-change="handlePageChange"
            />
        </BaseCard>
    </div>
</template>

<script>
import PaginationComponent from '@/components/common/PaginationComponent.vue';

export default {
    name: 'ProductsList',
    components: {
        PaginationComponent
    },
    props: {
        products: {
            type: Array,
            default: () => []
        },
        pagination: {
            type: Object,
            default: () => ({
                currentPage: 1,
                perPage: 10,
                total: 0,
                lastPage: 1,
                from: null,
                to: null
            })
        },
        loading: {
            type: Boolean,
            default: false
        },
        categories: {
            type: Array,
            default: () => []
        },
        lowStockProducts: {
            type: Array,
            default: () => []
        }
    },
    emits: ['page-change', 'search-change', 'category-change', 'low-stock-toggle', 'add-product', 'edit-product', 'delete-product'],
    directives: {
        clickOutside: {
            mounted(el, binding) {
                el.clickOutsideEvent = function(event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.body.addEventListener('click', el.clickOutsideEvent);
            },
            unmounted(el) {
                document.body.removeEventListener('click', el.clickOutsideEvent);
            }
        }
    },
    data() {
        return {
            filteredCategories: [],
            search: '',
            selectedCategory: '',
            categorySearchTerm: '',
            showCategoryDropdown: false,
            showLowStock: false,
            searchTimeout: null
        };
    },
    computed: {
        displayedProducts() {
            // Avec pagination côté serveur, on affiche directement les produits reçus
            if (!Array.isArray(this.products)) {
                return [];
            }
            return this.products;
        },
        hasActiveFilters() {
            return this.search || this.selectedCategory || this.showLowStock;
        },
        selectedCategoryName() {
            if (!this.selectedCategory) return '';
            const category = this.categories.find(c => c.id === this.selectedCategory);
            return category ? category.name : '';
        }
    },
    watch: {
        categories: {
            immediate: true,
            handler(newCategories) {
                this.filteredCategories = Array.isArray(newCategories) ? [...newCategories] : [];
            }
        }
    },
    methods: {
        onSearchInput() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.$emit('search-change', this.search);
            }, 300);
        },
        filterCategories() {
            const search = this.categorySearchTerm.toLowerCase();
            this.filteredCategories = this.categories.filter(cat =>
                cat.name.toLowerCase().includes(search)
            );
        },
        selectCategory(categoryId) {
            this.selectedCategory = categoryId;
            this.showCategoryDropdown = false;
            this.categorySearchTerm = categoryId ? this.getCategoryName(categoryId) : '';
            this.$emit('category-change', categoryId);
        },
        clearCategoryFilter() {
            this.selectedCategory = '';
            this.categorySearchTerm = '';
            this.filteredCategories = this.categories;
        },
        closeCategoryDropdown() {
            this.showCategoryDropdown = false;
        },
        toggleLowStockFilter() {
            this.showLowStock = !this.showLowStock;
            this.$emit('low-stock-toggle', this.showLowStock);
        },
        clearSearch() {
            this.search = '';
            this.$emit('search-change', '');
        },
        clearAllFilters() {
            this.search = '';
            this.selectedCategory = '';
            this.categorySearchTerm = '';
            this.showLowStock = false;
            this.$emit('search-change', '');
            this.$emit('category-change', '');
            this.$emit('low-stock-toggle', false);
        },
        getCategoryName(categoryId) {
            const category = this.categories.find(c => c.id === categoryId);
            return category ? category.name : '';
        },
        formatPrice(price) {
            if (!price) return '0 FCFA';
            return `${Number(price).toLocaleString('fr-FR')} FCFA`;
        },
        goToProductDetails(product) {
            this.$router.push(`/products/${product.id}`);
        },
        onAddProduct() {
            this.$emit('add-product');
        },
        onEditProduct(product) {
            this.$emit('edit-product', product);
        },
        onDeleteProduct(product) {
            this.$emit('delete-product', product);
        },
        handlePageChange(page) {
            this.$emit('page-change', page);
        }
    }
};
</script>

<style scoped>
/* Minimal scoped styles - Tailwind handles most styling */
</style>
